
import ActivationProgressBar from './ActivationProgressBar';
import ActivationCallToAction from './ActivationCallToAction';
import ActivationStepCard from './ActivationStepCard';
import { useActivation, ACTIVATION_STEPS } from '@/contexts/ActivationContext';

export default function ActivationSummary() {
  const { isStepComplete, completedCount, isLoading } = useActivation();

  const steps = ACTIVATION_STEPS.map((step, idx) => {
    const completed = isStepComplete(step.key);
    const prevCompleted = idx === 0 || isStepComplete(ACTIVATION_STEPS[idx - 1].key);
    let status: 'completed' | 'available' | 'locked';
    if (completed) {
      status = 'completed';
    } else if (!isLoading && prevCompleted) {
      status = 'available';
    } else {
      status = 'locked';
    }
    return { ...step, status };
  });

  // El total que se muestra en la barra debe coincidir con `completedCount` del
  // contexto, que sólo cuenta los pasos required:true (excluye scrapeCuentas,
  // que es informativo). Antes el total era ACTIVATION_STEPS.length (7) y
  // completedCount sumaba sobre 6 → la barra nunca llegaba a 100%.
  const requiredSteps = ACTIVATION_STEPS.filter((s) => s.required);
  const totalCount = requiredSteps.length;
  const allCompleted = !isLoading && completedCount === totalCount;
  const nextPendingStep = steps.find((s) => s.status === 'available') ?? null;
  const nextPendingHref = nextPendingStep?.href ?? null;
  const nextPendingTitle = nextPendingStep?.title ?? null;

  return (
    <div className="mx-auto w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[600px]">
      <div className="p-6 md:p-8">
        {/* Header consolidado: título + progreso + siguiente paso (cuando aplica)
            van juntos en el mismo bloque para ahorrar espacio vertical y que
            el comercio vea de un vistazo cuánto avanzó y qué le toca ahora. */}
        <div className="mb-8 border-b border-slate-100 dark:border-slate-800 pb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                {allCompleted ? '¡Cuenta activada!' : 'Resumen de Activación'}
              </h2>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {allCompleted
                  ? 'Tu cuenta está configurada. Ya puedes gestionar tus cuotas y recibir adelantos.'
                  : `Completa estos ${totalCount} pasos para habilitar todas las funcionalidades de adelanto de cuotas.`}
              </p>
            </div>
            {allCompleted && (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900/30">
                <span className="material-symbols-outlined text-base">check_circle</span>
                Activa
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="mt-4 h-2 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse" />
          ) : (
            <ActivationProgressBar completed={completedCount} total={totalCount} />
          )}
          {!allCompleted && !isLoading && (
            <div className="mt-4">
              <ActivationCallToAction nextPendingHref={nextPendingHref} nextPendingTitle={nextPendingTitle} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {steps.map((step) => (
            <ActivationStepCard
              key={step.key}
              icon={step.icon}
              title={step.title}
              subtitle={step.subtitle}
              status={step.status}
              href={step.href}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
